// Sostenibilidad (ESG): huella de desperdicio y transporte (CO2e) + raciones
// servidas, sobre datos reales. Reporte exportable. Catering → Resumen.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Leaf, Trash2, Truck, HeartHandshake, Cog, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering } from './shared';

interface Resumen {
  periodo: { desde: string; hasta: string };
  desperdicio: { raciones: number; kg: number; co2e_kg: number; pct: number };
  transporte: { km: number; despachos: number; co2e_kg: number };
  social: { raciones_servidas: number; raciones_despachadas: number };
  total: { co2e_kg: number; co2e_por_racion: number | null };
  config: { kg_por_racion: number; co2_kg_desperdicio: number; co2_km: number };
}
interface SerieDia { fecha: string; despachadas: number; devueltas: number; pct: number }

const primerDiaMes = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const isoHoy = (): string => new Date().toISOString().slice(0, 10);

export function EsgCatering() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(isoHoy());
  const [cfgOpen, setCfgOpen] = useState(false);

  const { data, isLoading } = useQuery<Resumen>({
    queryKey: ['cat-esg', desde, hasta],
    queryFn: () => catRpc<Resumen>('cat_esg_resumen', { _desde: desde, _hasta: hasta }),
  });
  const { data: serie = [] } = useQuery<SerieDia[]>({
    queryKey: ['cat-esg-serie', desde, hasta],
    queryFn: () => catRpc<SerieDia[]>('cat_esg_serie', { _desde: desde, _hasta: hasta }),
  });

  const guardarCfg = useMutation({
    mutationFn: (c: { kg: number; des: number; km: number }) => catRpc('cat_esg_config_set', { _kg_por_racion: c.kg, _co2_kg_desperdicio: c.des, _co2_km: c.km }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Factores actualizados'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const maxPct = Math.max(1, ...serie.map((s) => s.pct));

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Leaf className="h-4 w-4 text-emerald-600" /> Sostenibilidad (ESG)</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setCfgOpen((c) => !c)}><Cog className="h-4 w-4" /> Factores</Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => window.print()}><Printer className="h-4 w-4" /> Imprimir</Button>
          </div>
          {cfgOpen && data && (
            <div className="mt-2 rounded-md border p-2 text-xs">
              <CfgEditor cfg={data.config} saving={guardarCfg.isPending} onSave={(kg, des, km) => guardarCfg.mutate({ kg, des, km })} />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Calculando…</p>}
          {!isLoading && data && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Indic icon={<Trash2 className="h-5 w-5" />} tono="red" titulo="Desperdicio de alimentos"
                grande={`${num(data.desperdicio.co2e_kg, 1)} kg CO₂e`}
                sub={`${num(data.desperdicio.kg, 1)} kg comida · ${num(data.desperdicio.raciones, 0)} raciones · ${num(data.desperdicio.pct, 1)}%`} />
              <Indic icon={<Truck className="h-5 w-5" />} tono="amber" titulo="Huella de transporte"
                grande={`${num(data.transporte.co2e_kg, 1)} kg CO₂e`}
                sub={`${num(data.transporte.km, 1)} km · ${num(data.transporte.despachos, 0)} despachos`} />
              <Indic icon={<Leaf className="h-5 w-5" />} tono="emerald" titulo="Huella total"
                grande={`${num(data.total.co2e_kg, 1)} kg CO₂e`}
                sub={data.total.co2e_por_racion != null ? `${num(data.total.co2e_por_racion, 3)} kg CO₂e por ración servida` : 'sin raciones'} />
              <Indic icon={<HeartHandshake className="h-5 w-5" />} tono="sky" titulo="Impacto social"
                grande={`${num(data.social.raciones_servidas, 0)}`}
                sub={`raciones servidas de ${num(data.social.raciones_despachadas, 0)} despachadas`} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tendencia de desperdicio */}
      {!!serie.length && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Tendencia de desperdicio (% devuelto por día)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {serie.map((s) => (
                <div key={s.fecha} className="flex items-center gap-2 text-xs">
                  <span className="w-20 shrink-0 text-muted-foreground">{s.fecha.slice(5)}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded bg-muted">
                    <div className={`h-full ${s.pct > 5 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, (s.pct / maxPct) * 100)}%` }} />
                  </div>
                  <span className="w-28 shrink-0 text-right font-mono">{num(s.pct, 1)}% · {num(s.devueltas, 0)}/{num(s.despachadas, 0)}</span>
                </div>
              ))}
            </div>
            <p className="pt-2 text-[11px] text-muted-foreground">El desperdicio = raciones devueltas en el retorno de despacho. Bajarlo mejora el margen y la huella a la vez.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const TONO: Record<string, string> = {
  red: 'text-red-600', amber: 'text-amber-600', emerald: 'text-emerald-600', sky: 'text-sky-600',
};
function Indic({ icon, tono, titulo, grande, sub }: { icon: React.ReactNode; tono: string; titulo: string; grande: string; sub: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className={`flex items-center gap-2 text-xs font-medium ${TONO[tono]}`}>{icon} {titulo}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{grande}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function CfgEditor({ cfg, onSave, saving }: { cfg: { kg_por_racion: number; co2_kg_desperdicio: number; co2_km: number }; onSave: (kg: number, des: number, km: number) => void; saving: boolean }) {
  const [kg, setKg] = useState(String(cfg.kg_por_racion));
  const [des, setDes] = useState(String(cfg.co2_kg_desperdicio));
  const [km, setKm] = useState(String(cfg.co2_km));
  const changed = Number(kg) !== cfg.kg_por_racion || Number(des) !== cfg.co2_kg_desperdicio || Number(km) !== cfg.co2_km;
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block">kg por ración<input type="number" step="0.01" className={`${fieldCls} mt-0.5 w-24`} value={kg} onChange={(e) => setKg(e.target.value)} /></label>
      <label className="block">kg CO₂e por kg desperdicio<input type="number" step="0.1" className={`${fieldCls} mt-0.5 w-24`} value={des} onChange={(e) => setDes(e.target.value)} /></label>
      <label className="block">kg CO₂e por km<input type="number" step="0.01" className={`${fieldCls} mt-0.5 w-24`} value={km} onChange={(e) => setKm(e.target.value)} /></label>
      {changed && <Button size="sm" className="h-8" disabled={saving} onClick={() => onSave(Number(kg), Number(des), Number(km))}>Guardar</Button>}
    </div>
  );
}
